import classNames from 'classnames';
import React, { useMemo } from 'react';
import { SegmentButtonContext } from './SegmentButton.context';
import type { SegmentButtonPosition } from './SegmentButton/SegmentButton.types';
import { SegmentedButtonContext } from './SegmentedButton.context';
import type {
  SegmentedButtonMultiProps,
  SegmentedButtonSingleProps,
} from './SegmentedButton.types';
import { getValidChildren } from './SegmentedButtonHelpers';

const Multi: React.FC<SegmentedButtonMultiProps> = (props) => {
  const handleButtonClick = useMemo(
    () => (value: string) => {
      let updatedSelection: string[];

      // In multiple selection mode, toggle the clicked button
      if (props.value.includes(value)) {
        updatedSelection = props.value.filter((selected) => selected !== value);
      } else {
        updatedSelection = [...props.value, value];
      }

      props.onChange(updatedSelection, value);
    },
    [props.value, props.onChange],
  );

  const context = React.useMemo(
    () => ({
      size: props.size,
      variant: props.variant,
      onChange: handleButtonClick,
      selectedButtons: props.value,
    }),
    [props.size, props.variant, handleButtonClick, props.value],
  );

  const validChildren = getValidChildren(props.children);

  const getPosition = (index: number): SegmentButtonPosition => ({
    first: index === 0,
    last: index === validChildren.length - 1,
  });

  return (
    <SegmentedButtonContext.Provider value={context}>
      <div
        ref={props.innerRef}
        className={classNames(
          'flex-nowrap relative inline-flex',
          props.className,
        )}
      >
        {validChildren.map((child, index) => {
          return (
            <SegmentButtonContext.Provider
              key={child.props.value}
              value={getPosition(index)}
            >
              {child}
            </SegmentButtonContext.Provider>
          );
        })}
      </div>
    </SegmentedButtonContext.Provider>
  );
};

const Single = ({
  value,
  onChange,
  ...otherProps
}: SegmentedButtonSingleProps) => {
  const multiValue = useMemo(() => (value === null ? [] : [value]), [value]);

  return (
    <Multi
      value={multiValue}
      onChange={(_value, toggled) => {
        if (toggled !== value) {
          onChange(toggled);
        }
      }}
      {...otherProps}
    />
  );
};

export const SegmentedButton = {
  Single,
  Multi,
};
